import "./base.css";
import "./polyfills/promise.polyfill";

import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import retainCriticalData from "./app/retainCriticalData";
import { CrossTabCoordinator } from "./app/session/CrossTabCoordinator";
import store from "./app/store";
import Application from "./features/application/Application";
import ErrorPage from "./features/errorHandling/ErrorPage";
import { NavigationVisibilityProvider } from "./features/navigation/NavigationVisibilityProvider";
import IntlProviderWrapper from "./i18n/IntlProviderWrapper";
import isOneWebDomainRuntime from "./shared/avaAppRuntime";
import { setupAvaAppTokenInterceptors } from "./shared/avaAppToken";
import { AVA_APP_NAME, ERROR_PAGE_TYPES } from "./shared/constants";
import { cleanupLogger } from "./shared/Logger";
import { cleanupSessionRefreshTimeout } from "./shared/sessionTimeoutCleanup";
import AppThemeProvider from "./theme/AppThemeProvider";
import { getRuntimeConfig, loadRuntimeConfig } from "./utils/runtimeConfig";

// Type guard to ensure container element exists
const getContainer = (): HTMLElement => {
    const container = isOneWebDomainRuntime()
        ? document.querySelector("main")
        : document.getElementById("root");

    if (!container) {
        throw new Error(
            isOneWebDomainRuntime()
                ? "Could not find <main> element for One Web Domain runtime"
                : "Could not find element with id 'root'"
        );
    }

    return container as HTMLElement;
};

// Initialize page hide event listener for logger cleanup (all runtimes)
window.addEventListener("pagehide", () => {
    // Clean up session refresh timeout to prevent memory leaks
    cleanupSessionRefreshTimeout();

    // Clean up cross-tab coordination keys before page unload
    // This ensures locks don't persist if the tab crashes or is force-closed
    CrossTabCoordinator.cleanupCoordinationKeys();

    // Retain critical data only for One Web Domain runtime (preserve original behavior)
    if (isOneWebDomainRuntime()) {
        retainCriticalData();
    }

    // Cleanup logger for all runtimes
    cleanupLogger();
});

// Initialize One Web Domain specific scripts
if (isOneWebDomainRuntime()) {
    setupAvaAppTokenInterceptors();
    import("./scripts/custom-elements");
    import("./scripts/qualtrics-tracking");
    import("./scripts/storage-utils");
    import("./scripts/retain-data");
}

/**
 * Render the React application
 */
function renderApp() {
    const container: HTMLElement = getContainer();

    // Add classes to main element when running in One Web Domain runtime
    if (isOneWebDomainRuntime()) {
        container.classList.add("main-content", "margin-top-lg");
    }

    const basename = isOneWebDomainRuntime() ? AVA_APP_NAME : "";

    const root: Root = createRoot(container);

    const appContent = (
        <IntlProviderWrapper>
            <HelmetProvider>
                <NavigationVisibilityProvider>
                    <BrowserRouter basename={basename}>
                        <Application />
                    </BrowserRouter>
                </NavigationVisibilityProvider>
            </HelmetProvider>
        </IntlProviderWrapper>
    );

    const content: React.ReactNode = (
        <Provider store={store}>
            <AppThemeProvider>{appContent}</AppThemeProvider>
        </Provider>
    );

    root.render(<React.StrictMode>{content}</React.StrictMode>);
}

/**
 * Render an error screen when configuration fails to load
 */
function renderConfigError() {
    const container: HTMLElement = getContainer();
    const root: Root = createRoot(container);

    const errorPage = <ErrorPage type={ERROR_PAGE_TYPES.CONFIG_ERROR} />;

    root.render(
        <React.StrictMode>
            <Provider store={store}>
                <NavigationVisibilityProvider>
                    <AppThemeProvider>{errorPage}</AppThemeProvider>
                </NavigationVisibilityProvider>
            </Provider>
        </React.StrictMode>
    );
}

/**
 * Initialize the application
 *
 * For standalone deployments: Load runtime configuration before rendering
 * For OneWebDomain: Skip runtime config (uses window.AvaAppConfig)
 */
async function initializeApp() {
    try {
        // Load runtime configuration for standalone deployments
        // Skip for OneWebDomain as it uses window.AvaAppConfig
        if (!isOneWebDomainRuntime()) {
            // eslint-disable-next-line no-console
            console.info("🔧 Loading runtime configuration...");
            await loadRuntimeConfig();

            // Set window.aui.env for Skylab SDK to determine correct data service URLs
            // The SDK uses this to choose between dev/qa/sbx/prd endpoints
            const runtimeConfig = getRuntimeConfig();
            const appEnv = runtimeConfig?.VITE_ENV || "local";
            // Map 'local' to 'qa' for Skylab SDK compatibility (it recognizes: dev, local, ci, qa, uat, sbx, prod)
            window.aui = { env: appEnv === "local" ? "qa" : appEnv };
        }

        // Render the application
        renderApp();
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("❌ Failed to initialize application:", error);
        renderConfigError();
    }
}

// Start the application
initializeApp();
